import React, { useState } from 'react'
import { ICompiledReportInfo } from '../../definitions/report_builder/ReportBuilderDefinitions'
import { useAppDispatch, useAppSelector } from '../../store/Hooks'
import useQuery, { buildQueryKey } from '../hooks/useQuery'
import ApiClient from '../../backend/ApiClient'
import { COMPILED_CONSTANTS } from '../../CompiledConstants'
import { snackbar } from '../services/SnackbarService'
import { changeRoute } from '../../store/slices/SessionSlice'
import { Avatar, Box, Card, Chip, Collapse, Dialog, DialogContent, DialogTitle, IconButton, Modal, Skeleton, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography, Grid, Tooltip } from '@mui/material'
import { CloseRounded, InfoRounded, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'
import PDFViewer from '../controls/PDFViewer'
import Formatter from '../../helpers/Formatter'
import { statusOptions } from '../../definitions/Approval'
import SmartCard from '../controls/cards/SmartCard'

interface P {
    contractId: number
}

export default function ContractReports({ contractId }: P) {
    const [openPeriods, setOpenPeriods] = useState<Record<string, boolean>>({})
    const [modalOpen, setModalOpen] = useState(false)
    const [selectedReport, setSelectedReport] = useState<ICompiledReportInfo>(null)
    const mode = useAppSelector(state => state.app.mode)
    const [pdfUrl, setPdfUrl] = useState<string>(null)
    const [pdfLoading, setPdfLoading] = useState<boolean>(false)
    const [openPreview, setOpenPreview] = useState<boolean>(false)
    const dispatch = useAppDispatch()

    const { response, status } = useQuery<Record<string, ICompiledReportInfo[]>>({
        queryKey: buildQueryKey(`compiledReports-${contractId}`),
        queryFn: () => ApiClient.getContractReports(contractId),
        cache: true
    })

    const { response: getReports } = useQuery({
        queryKey: buildQueryKey('reports'),
        queryFn: () => ApiClient.getReports(),
        cache: true
    })

    const togglePeriod = (period: string) => {
        setOpenPeriods(prev => ({ ...prev, [period]: !prev[period] }))
    }

    const getPdf = async (fileKey: string) => {
        try {
            setPdfLoading(true)
            const response = await ApiClient.getPdfPresignedUrl(fileKey, COMPILED_CONSTANTS.evolvereportsBucketName)
            setPdfUrl(response.url)
            setOpenPreview(true)
        } catch (error) {
            snackbar.show({ severity: 'error', text: `Error during load PDF, ${error}` })
        } finally {
            setPdfLoading(false)
        }
    }

    const handleDispatchToTemplate = (reportUUID: string) => {
        if (!getReports) return
        dispatch(changeRoute({
            route: 'report-editor',
            params: { report: getReports.find(r => r.uuid === reportUUID) }
        }))
    }

    const handleDispatchToFlowExecutionEnv = (flowId: string, executionEnvUUID: string) => {
        dispatch(changeRoute({
            route: 'flow',
            params: { flowId: flowId, eeId: executionEnvUUID }
        }))
    }

    if (status === 'loading') {
        return (
            <SmartCard table={true} sx={{ mt: 2 }}>
                <Table>
                    <TableBody>
                        {Array.from({ length: 7 }).map((_, idx) => (
                            <TableRow key={idx}>
                                <TableCell>
                                    <Skeleton variant='circular' width={24} height={24} />
                                </TableCell>
                                <TableCell>
                                    <Skeleton width={80} />
                                </TableCell>
                                <TableCell align='right'>
                                    <Skeleton width={60} />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </SmartCard>
        )
    }

    return (
        <SmartCard sx={{ mt: 2 }}>
            <Table stickyHeader size='medium'>
                <TableHead sx={{ backgroundColor: mode === 'light' ? 'rgba(0, 0, 0, 0.87)' : undefined }}>
                    <TableRow>
                        <TableCell width={20} />
                        <TableCell sx={{ color: mode === 'light' ? 'common.white' : 'inherit' }}>Period</TableCell>
                        <TableCell align='right' sx={{ color: mode === 'light' ? 'common.white' : 'inherit' }}>Reports Count</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {Object?.entries(response || {}).map(([period, reports]) => (
                        <React.Fragment key={period}>
                            <TableRow
                                hover
                                onClick={() => togglePeriod(period)}
                                sx={{
                                    cursor: 'pointer',
                                    bgcolor: openPeriods[period] ? (mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.12)') : 'inherit'
                                }}>
                                <TableCell>
                                    <IconButton size='small'>
                                        {openPeriods[period] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                    </IconButton>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>{period}</TableCell>
                                <TableCell align='right'>
                                    <Chip label={`${reports?.length} Items`} size='small' variant='outlined' />
                                </TableCell>
                            </TableRow>

                            <TableRow>
                                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                                    <Collapse in={openPeriods[period]} timeout='auto' unmountOnExit>
                                        <Box sx={{ margin: 1, mb: 2, overflow: 'auto', maxHeight: '350px' }}>
                                            <Table size='medium' stickyHeader>
                                                <TableHead sx={{ '& th': { backgroundColor: '#272727' } }}>
                                                    <TableRow sx={{ '& th': { color: 'text.secondary', fontWeight: 600 } }}>
                                                        <TableCell>PDF Name</TableCell>
                                                        <TableCell>Run Date</TableCell>
                                                        <TableCell>Last Run By</TableCell>
                                                        <TableCell>Report Template</TableCell>
                                                        <TableCell align='center'>Details</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {reports?.map(report => (
                                                        <TableRow
                                                            hover
                                                            sx={{ cursor: 'pointer', overflow: 'auto' }}
                                                            onClick={() => getPdf(report.fileKey)}
                                                            key={report?.uuid}>
                                                            <TableCell>{report?.reportName || 'Report'}</TableCell>
                                                            <TableCell>{Formatter.date(new Date(report?.createdAt), 'mm/dd/yyyy hh:mm:ss')}</TableCell>
                                                            <TableCell>
                                                                <Stack direction='row' spacing={1} alignItems='center'>
                                                                    <Avatar sx={{ width: 22, height: 22, fontSize: 11 }}>
                                                                        {report?.runnedBy?.charAt(0)?.toUpperCase() ?? ''}
                                                                    </Avatar>
                                                                    <Typography variant='body2'>{report.runnedBy}</Typography>
                                                                </Stack>
                                                            </TableCell>

                                                            <TableCell
                                                                onClick={e => {
                                                                    e.stopPropagation()
                                                                    handleDispatchToTemplate(report?.reportUUID)
                                                                }}
                                                                sx={{ 'cursor': 'pointer', ':hover': { textDecoration: 'underline' } }}>
                                                                <Tooltip title='Open Report Template' placement='bottom' arrow={true}>
                                                                    <Box sx={{ width: 'fit-content' }}>
                                                                        <Typography variant='body2'>
                                                                            {report?.reportTemplateName || report?.reportUUID}
                                                                        </Typography>
                                                                    </Box>
                                                                </Tooltip>
                                                            </TableCell>
                                                            <TableCell align='center'>

                                                                <IconButton
                                                                    size='small'
                                                                    onClick={e => {
                                                                        e.stopPropagation()
                                                                        setSelectedReport(report)
                                                                        setModalOpen(true)
                                                                    }}>
                                                                    <InfoRounded fontSize='small' />
                                                                </IconButton>

                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </Box>
                                    </Collapse>
                                </TableCell>
                            </TableRow>
                        </React.Fragment>
                    ))}
                </TableBody>
            </Table>

            <Dialog open={modalOpen} onClose={() => setModalOpen(false)} PaperProps={{ sx: { width: 500, borderRadius: 2 } }}>
                <DialogTitle sx={{ mb: 1 }}>
                    <Stack flexDirection='row' alignItems='center' justifyContent='space-between'>
                        <Typography variant='h6'>Info</Typography>
                        <IconButton size='small' onClick={() => setModalOpen(false)}>
                            <CloseRounded fontSize='small' />
                        </IconButton>
                    </Stack>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedReport?.approval && Object.keys(selectedReport?.approval).length > 0
                        ? (
                                <Grid container spacing={3}>
                                    <Grid item xs={6}>
                                        <Stack spacing={2}>
                                            <Box>
                                                <Typography variant='caption' color='text.secondary' display='block'>Contract</Typography>
                                                <Typography variant='body2'>Version {selectedReport?.contractVersion}</Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant='caption' color='text.secondary' display='block'>Settlement</Typography>
                                                <Typography variant='body2'>{selectedReport?.approval?.entityName}</Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant='caption' color='text.secondary' display='block'>Settlement Version</Typography>
                                                <Typography variant='body2'>{selectedReport?.settlementVersion?.version}</Typography>
                                            </Box>
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Stack spacing={2}>
                                            <Box>
                                                <Typography variant='caption' color='text.secondary' display='block'>Status</Typography>
                                                <Chip
                                                    sx={{
                                                        borderRadius: '999px',
                                                        fontWeight: 600
                                                    }}
                                                    label={(
                                                        <Box display='flex' alignItems='center' gap={1}>
                                                            <Box
                                                                width={8}
                                                                height={8}
                                                                borderRadius='50%'
                                                                bgcolor={
                                                                    (selectedReport.approval?.status === 'approved' && selectedReport.settlementVersion.isHead)
                                                                        ? 'success.main'
                                                                        : (selectedReport.approval?.status === 'approved' && !selectedReport.settlementVersion.isHead)
                                                                                ? 'grey.500'
                                                                                : selectedReport.approval?.status === 'ready-for-review'
                                                                                    ? 'info.main'
                                                                                    : selectedReport.approval?.status === 'rejected'
                                                                                        ? 'error.main'
                                                                                        : 'warning.main'
                                                                }
                                                            />
                                                            <Typography variant='caption' sx={{ fontWeight: 600 }}>
                                                                {statusOptions.find(option => option.value === selectedReport.approval?.status)?.label ?? 'Draft'}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                    variant='outlined'
                                                    size='small'
                                                />
                                            </Box>
                                            <Box>
                                                <Typography variant='caption' color='text.secondary' display='block'>Requested by</Typography>
                                                <Stack flexDirection='row' gap={1} alignItems='center' mt={0.5}>
                                                    <Avatar sx={{ width: 20, height: 20, fontSize: 11 }}>
                                                        {(selectedReport?.approval?.changes?.at(-2)?.userName ?? '')?.charAt(0)?.toUpperCase()}
                                                    </Avatar>
                                                    <Typography variant='body2'>{selectedReport?.approval?.changes?.at(-2)?.userName ?? '-'}</Typography>
                                                </Stack>
                                            </Box>
                                            <Box>
                                                <Typography variant='caption' color='text.secondary' display='block'>Approved by</Typography>
                                                <Stack flexDirection='row' gap={1} alignItems='center' mt={0.5}>
                                                    {selectedReport?.approval?.status === 'approved' && selectedReport.approval?.changes?.at(-1)
                                                        ? (
                                                                <Avatar sx={{ width: 20, height: 20, fontSize: 11 }}>
                                                                    {selectedReport?.approval?.changes?.at(-1)?.userName?.charAt(0).toUpperCase()}
                                                                </Avatar>
                                                            )
                                                        : null}
                                                    <Typography variant='body2'>
                                                        {selectedReport?.approval?.status === 'approved' && selectedReport.approval?.changes?.at(-1)?.userName
                                                            ? selectedReport?.approval?.changes?.at(-1)?.userName
                                                            : '-'}
                                                    </Typography>
                                                </Stack>
                                            </Box>
                                        </Stack>
                                    </Grid>
                                </Grid>
                            )
                        : (
                                <Grid container spacing={3}>
                                    <Grid item xs={6}>
                                        <Stack spacing={2}>
                                            <Box>
                                                <Typography variant='caption' color='text.secondary' display='block'>Engine</Typography>
                                                <Typography
                                                    variant='body2'
                                                    onClick={e => {
                                                        e.preventDefault()
                                                        handleDispatchToFlowExecutionEnv(selectedReport.flowUUID, selectedReport.executionEnviromentUUID)
                                                    }}
                                                    sx={{ 'cursor': 'pointer', '&:hover': { textDecoration: 'underline' }, 'color': 'primary' }}>
                                                    {selectedReport?.flowName ?? '-'}
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant='caption' color='text.secondary' display='block'>Engine Version</Typography>
                                                <Typography variant='body2'>Version {selectedReport?.flowVersion?.version ?? -''}</Typography>
                                            </Box>
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Box>
                                            <Typography variant='caption' color='text.secondary' display='block'>Contract</Typography>
                                            <Typography variant='body2'>Version {selectedReport?.contractVersion ?? '-'}</Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            )}
                </DialogContent>
            </Dialog>
            {!pdfLoading && (
                <Modal open={openPreview} onClose={() => setOpenPreview(false)} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Card sx={{ height: '90vh', width: '90vw', position: 'relative' }}>
                        <PDFViewer file={pdfUrl} />
                    </Card>
                </Modal>
            )}
        </SmartCard>
    )
}
